import { v4 as uuidv4 } from 'uuid';
import appManager from '../../app/appManager';
import AppModule from '../../app/lib/AppModule';
import { diagnosticsState } from './lib/state';
import {
  TrackErrorInput,
  TrackEventInput,
  TrackedError,
  TrackedErrorPriority,
  TrackedErrorType,
  TrackedEvent,
  FrontendMetricEntry,
  MetricType,
} from './lib/types';
import * as Sentry from '@sentry/react';
import { sentryErrorIntegration } from './SentryErrorIntegration';
import extractErrorMessage from '@lib/utils/extractErrorMessage';
import environment from '@lib/environment';
import AuthModule from '@modules/AuthModule';
import FeatureFlagModule from '@modules/FeatureFlagModule';
import { FeatureFlagKeys } from '@modules/FeatureFlagModule/featureFlagKeys';
import { CallDirection } from '@api/generated/graphql';

// Re-export CallDirection for convenience
export { CallDirection };

const ERROR_IGNORE_LIST = [
  'window.require is not implemented', // workaround for vite preview (see index.html)
  'Requested device not found', // side effect of MFE soft phone
];

/**
 * Metric name constants for frontend metrics.
 * These names will be prefixed with "contactcenter_fe_" by the BFF.
 */
export const MetricNames = {
  ERROR_TOTAL: 'error_total',
  LATENCY_DURATION: 'latency_duration',
} as const;

/**
 * Metric tag constants for categorizing metrics.
 * Use LOW cardinality values only!
 */
export const MetricTags = {
  OPERATION_NAME: 'operation_name',
  ERROR_TYPE: 'error_type',
  DIRECTION: 'direction',
} as const;

/**
 * Operation names for metrics.
 */
export const OperationNames = {
  CLASSIFICATION: 'classification',
  SESSION_OFFERED: 'session-offered',
  CALL_PLACED: 'call-placed',
  CALL_CONNECTION: 'call-connection',
} as const;

/**
 * Error types for classification errors.
 */
export const ClassificationErrorTypes = {
  STATUS_NULL: 'status_null',
  NOT_ENDED: 'not_ended',
  NOT_READY: 'not_ready',
  UNHANDLED_CLASSIFICATION: 'unhandled_classification',
  EXCEPTION: 'exception',
} as const;

/**
 * Error types for call connection failures.
 * Only used when success=false.
 */
export const CallConnectionErrorTypes = {
  TIMEOUT: 'timeout',
  GENERAL: 'general',
  WARM_TRANSFER: 'warm_transfer',
} as const;

/**
 * Error category values for error classification.
 * LOW cardinality categories to avoid metric explosion.
 * Used for categorizing errors in metrics tags.
 */
export const ServerErrorCategory = {
  TIMEOUT: 'timeout',
  NETWORK: 'network',
  AUTH: 'auth',
  SERVER_ERROR: 'server_error',
  BAD_REQUEST: 'bad_request',
  UNKNOWN: 'unknown',
} as const;

function isIgnoredError(errorMessage: string) {
  return ERROR_IGNORE_LIST.some((ignore) => errorMessage.includes(ignore));
}

function trackError(errorInput: TrackErrorInput) {
  let error: Error;
  if (!errorInput.error) {
    error = new Error(errorInput.message);
  } else if (errorInput.error instanceof Error) {
    error = errorInput.error;
  } else {
    error = new Error(errorInput.message, { cause: errorInput.error });
  }

  // ignore errors from our ignore list
  if (isIgnoredError(error.message)) {
    return;
  }

  // send to Sentry (only in non-dev/test environments)
  if (errorInput.source !== 'sentry') {
    // Only send to Sentry in production-like environments
    // Skip in local development and test modes to avoid noise
    if (environment.MODE !== 'development' && environment.MODE !== 'test') {
      Sentry.captureException(error);
    } else {
      console.warn(
        `[DiagnosticsModule] Sentry disabled in ${environment.MODE} mode. Error would have been reported:`,
        error.message,
      );
    }
  }

  // emit event so other modules can use it
  appManager.events.emit('diagnostics.error', {
    key: 'diagnostics',
    message: error.message,
    args: [errorInput.extra, errorInput.source, errorInput.priority],
  });

  const item: TrackedError = {
    id: uuidv4(),
    ...errorInput,
    priority: errorInput.priority || 'normal',
    error,
    createdAt: Date.now(),
    type: 'error',
    read: false,
  };
  diagnosticsState.errors = [...diagnosticsState.errors, item];
}

export type ReportErrorOptions = Omit<
  TrackErrorInput,
  'errorType' | 'message' | 'error'
>;

export type ReportEventOptions = Omit<TrackEventInput, 'message'>;

export class DiagnosticsModule extends AppModule {
  override events = ['diagnostics.error'];

  private metricsBuffer: FrontendMetricEntry[] = [];
  private readonly METRICS_FLUSH_INTERVAL_MS = 60000; // 1 minute
  private readonly METRICS_MAX_BUFFER_SIZE = 50;
  private readonly METRICS_ENDPOINT = `${environment.API_URL}/v1/metric`;
  private readonly MAX_BACKOFF_MULTIPLIER = 4; // Max 4 minutes between retries
  private isFlushingMetrics = false;
  private metricsFailureCount = 0;
  private metricsFlushesToSkip = 0;

  override preload(): void {
    // report event so we can see in UI
    this.app.events.onAny((event: string | string[], data: any) => {
      const message = Array.isArray(event) ? event.join(', ') : event;
      this.reportEvent(message, {
        source: 'app',
        extra: data,
      });
      this.app.logger.info('event', event, data);
    });

    // listen for console errors
    // these are handled, log.error(...) calls
    this.app.logger.on('error', (key, message, args) => {
      // report so it goes through the normal error flow
      this.reportBackgroundError(message, {
        source: key,
        priority: 'normal',
        extra: args,
      });
    });

    // Start metrics auto-flush
    this.startMetricsAutoFlush();
  }

  public reportUserError(message: string, options?: ReportErrorOptions): void;
  public reportUserError(error: unknown, options?: ReportErrorOptions): void;
  public reportUserError(
    error: unknown,
    message: string,
    options?: ReportErrorOptions,
  ): void;
  public reportUserError(
    error: any,
    message: any,
    options?: ReportErrorOptions,
  ): void {
    this.reportError(error, message, options, 'user');
  }

  public reportBackgroundError(
    message: string,
    options?: ReportErrorOptions,
  ): void;
  public reportBackgroundError(
    error: unknown,
    options?: ReportErrorOptions,
  ): void;
  public reportBackgroundError(
    error: unknown,
    message: string,
    options?: ReportErrorOptions,
  ): void;
  public reportBackgroundError(
    error: any,
    message: any,
    options?: ReportErrorOptions,
  ): void {
    this.reportError(error, message, options, 'background');
  }

  public reportEvent(message: string, options: ReportEventOptions) {
    const item: TrackedEvent = {
      extra: options.extra && this.cleanExtraData(options.extra),
      source: options.source,
      id: options.id ?? uuidv4(),
      message,
      createdAt: Date.now(),
      type: 'event',
    };
    diagnosticsState.events = [...diagnosticsState.events, item];
  }

  public clearItems() {
    diagnosticsState.errors = [];
    diagnosticsState.events = [];
  }

  public markErrorsAsRead(priorities?: TrackedErrorPriority[]) {
    diagnosticsState.errors.forEach((error) => {
      if (!priorities || priorities.includes(error.priority)) {
        error.read = true;
      }
    });
  }

  public setDiagnosticsDrawerOpen(open: boolean) {
    diagnosticsState.drawerOpen = open;
  }

  /**
   * Sentry integration that tracks errors through DiagnosticsModule.
   * Use this when configuring Sentry.init() integrations.
   * See {@link sentryErrorIntegration} for more details.
   */
  public static sentryIntegration() {
    return sentryErrorIntegration(trackError);
  }

  private reportError(
    arg1: any,
    arg2: any,
    arg3?: ReportErrorOptions,
    errorType: TrackedErrorType = 'user',
  ): void {
    let actualMessage: string | undefined = arg2;
    let actualError: unknown = arg1;

    if (typeof arg2 === 'object') {
      // arg2 is options, arg1 is a message or error
      arg3 = arg2;
      actualMessage = typeof arg1 === 'string' ? arg1 : undefined;
      actualError = typeof arg1 !== 'string' ? arg1 : undefined;
    }

    if (!actualMessage) {
      actualMessage = extractErrorMessage(actualError);
    }

    trackError({
      message: actualMessage,
      error: actualError,
      ...arg3,
      errorType,
    });
  }

  /**
   * Sends a metric to be tracked. The metric will be buffered and sent periodically to the BFF.
   * Metrics are only sent if the feature-front-end-metrics feature flag is enabled.
   *
   * @param name - The name of the metric (will be prefixed with "contactcenter_fe_" by the BFF)
   * @param type - The type of metric ("Counter" or "Histogram")
   * @param value - The value to record
   * @param tags - Optional tags for the metric (use LOW cardinality tags only!)
   */
  public sendMetric(
    name: string,
    type: MetricType,
    value: number,
    tags?: Record<string, string>,
  ): void {
    const metric: FrontendMetricEntry = {
      name,
      type,
      value,
      tags,
      timestamp: new Date().toISOString(),
    };

    this.metricsBuffer.push(metric);

    // Auto-flush if buffer is full AND not in backoff
    if (
      this.metricsBuffer.length >= this.METRICS_MAX_BUFFER_SIZE &&
      this.metricsFlushesToSkip === 0
    ) {
      this.flushMetrics();
    }
  }

  /**
   * Increments a counter metric by 1.
   *
   * @param name - The name of the counter metric
   * @param tags - Optional tags for the metric
   */
  public incrementCounter(name: string, tags?: Record<string, string>): void {
    this.sendMetric(name, 'Counter', 1, tags);
  }

  /**
   * Records a histogram metric value.
   *
   * @param name - The name of the histogram metric
   * @param value - The value to record
   * @param tags - Optional tags for the metric
   */
  public recordHistogram(
    name: string,
    value: number,
    tags?: Record<string, string>,
  ): void {
    this.sendMetric(name, 'Histogram', value, tags);
  }

  /**
   * Starts the automatic periodic flushing of metrics.
   */
  private startMetricsAutoFlush(): void {
    setInterval(() => this.flushMetrics(), this.METRICS_FLUSH_INTERVAL_MS);
  }

  /**
   * Flushes the metrics buffer to the BFF endpoint.
   * Only sends metrics if the feature-front-end-metrics feature flag is enabled.
   * Implements incremental backoff on failures: 60s, 120s, 180s, 240s.
   */
  private async flushMetrics(): Promise<void> {
    // Prevent concurrent flushes (race condition protection)
    if (this.isFlushingMetrics) {
      return;
    }

    // Implement backoff: skip flushes during backoff period
    if (this.metricsFlushesToSkip > 0) {
      this.metricsFlushesToSkip--;
      return;
    }

    if (this.metricsBuffer.length === 0) {
      return;
    }

    this.isFlushingMetrics = true;

    try {
      // Check if metrics feature is enabled
      const { value: metricsEnabled } = FeatureFlagModule.getFeatureFlagValue(
        FeatureFlagKeys.featureFrontEndMetrics,
      );

      if (!metricsEnabled) {
        // Clear buffer if feature is disabled
        this.metricsBuffer = [];
        return;
      }

      const token = await AuthModule.getCurrentToken();
      if (!token) {
        this.app.logger.warn(
          'No auth token available, skipping metrics flush. Will retry on next flush.',
        );
        // Don't clear buffer - will retry on next flush
        return;
      }

      // Only clear buffer after we know we can send
      const batch = [...this.metricsBuffer];
      this.metricsBuffer = [];

      const response = await fetch(this.METRICS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(batch),
      });

      if (!response.ok) {
        // Restore metrics to buffer on failure for retry
        this.metricsBuffer.unshift(...batch);
        // Increment failure count and set backoff (capped at MAX)
        this.metricsFailureCount = Math.min(
          this.metricsFailureCount + 1,
          this.MAX_BACKOFF_MULTIPLIER,
        );
        // Skip next N flushes (1, 2, 3, or 4) for incremental backoff
        this.metricsFlushesToSkip = this.metricsFailureCount;
        this.app.logger.error(
          'Failed to send metrics, will retry with backoff',
          response.status,
          response.statusText,
        );
      } else {
        // Reset failure count and backoff on success
        this.metricsFailureCount = 0;
        this.metricsFlushesToSkip = 0;
      }
    } catch (error) {
      // Increment failure count and set backoff on exception
      this.metricsFailureCount = Math.min(
        this.metricsFailureCount + 1,
        this.MAX_BACKOFF_MULTIPLIER,
      );
      this.metricsFlushesToSkip = this.metricsFailureCount;
      // Silently fail - metrics are non-critical
      this.app.logger.error('Metrics flush error', error);
    } finally {
      this.isFlushingMetrics = false;
    }
  }

  /**
   * Cleans the extra data by performing a deep copy using JSON serialization.
   * If the serialization fails, it returns the original data.
   *
   * @param extra - The extra data to be cleaned.
   * @returns The cleaned extra data or the original data if serialization fails.
   */
  private cleanExtraData(extra: any) {
    try {
      return JSON.parse(JSON.stringify(extra));
    } catch (_e) {
      return extra;
    }
  }
}

export default new DiagnosticsModule(
  appManager.events,
  appManager.logger('Diagnostics'),
);
